/* eslint-disable no-nested-ternary */

// Importing necessary libraries and components
import React from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import {
  useLocation,
  useHistory,
  Link,
} from "react-router-dom";
import ListIcon from "@material-ui/icons/List";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import Button from "components/CustomButtons/Button";
import { useTheme } from "@material-ui/core/styles";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import { setBackToSearchFromDetails } from "Redux/Actions/merchantInformation";

import styles from "assets/jss/material-dashboard-react/components/headerStyle";
import AdminNavbarLinks from "./AdminNavbarLinks";
import { logout } from "../../Redux/Actions/LoginActions";

// Applying custom styles using Material-UI's makeStyles
const useStyles = makeStyles(styles);

function Header(props) {
  const history = useHistory(); // React Router's history object for navigation
  const location = useLocation(); // React Router's location object for current URL
  const url = location.pathname; // Current URL path
  const theme = useTheme(); // Material-UI theme object
  const windowWidth = useMediaQuery(theme.breakpoints.up("md")); // Check if the screen width is medium or larger
  const [temp, setTemp] = React.useState(false); // Temporary state variable
  const classes = useStyles();
  const { color } = props; // Destructuring color prop
  const appBarClasses = classNames({
    [` ${classes[color]}`]: color, // Dynamically apply color class to AppBar
  });

  // Handles back navigation for mobile view
  const handleBackMobile = () => {
    history.goBack(); // Navigate to the previous page
    props.setBackToSearchFromDetails(); // Dispatch Redux action
    setTemp(!temp); // Toggle temp state
  };

  // Handles closing of the drawer
  const handleClose = () => {
    props.setopen(); // Dispatch Redux action to toggle drawer state
    if (props.openDrawer) {
      props.setOpenDrawerHover(false); // Disable hover effect if drawer is open
    }
  };

  // Generate the modal header based on the current URL and app name
  let modalHead = "";
  const urlName = history?.location?.pathname?.split("/")[2]?.toLowerCase();
  const pageName = props?.app?.split("/")[1]?.toLowerCase();
  const pageUrl = urlName?.includes("-") && urlName?.replace(/-/g, " ");

  if (urlName === pageName) {
    modalHead = props.app;
  } else if (urlName?.includes("-")) {
    modalHead = `${props.app.split("/")[0]}/${" "}${pageUrl}`;
  } else {
    modalHead = `${props.app.split("/")[0]}/${" "}${
      history.location.pathname.split("/")[2]
    }`;
  }
  modalHead = modalHead.replace(/([a-z])([A-Z])/g, "$1 $2"); // Add spaces between camel case words

  // Check if the current URL matches the modal header
  const includeHistory = history?.location?.pathname
    ?.split("/")[2]
    ?.toLowerCase()
    ?.includes(modalHead.replace(/\s+/g, "")?.split("/")[1]?.toLowerCase());

  // Generate breadcrumb items for navigation
  const segments = url.split("/").filter(segment => segment !== ""); // Split URL by '/' and remove empty segments
  const breadcrumbItems = [];
  let accumulatedPath = "";

  const generateDisplayName = segment => {
    // If segment contains hyphens, convert them to capitalized words
    if (segment.includes("-")) {
      return segment
        .split("-")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
    }

    // Convert camel case or uppercase words into separate words
    return segment
      .replace(/([a-z])([A-Z])/g, "$1 $2") // Add space before uppercase letters
      .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2") // Handle consecutive uppercase letters
      .replace(/\b[A-Z]+\b/g, match => match.charAt(0) + match.slice(1).toLowerCase()) // Capitalize first letter, lowercase rest
      .trim(); // Remove any trailing spaces
  };

  for (let i = 0; i < segments.length; i++) {
    accumulatedPath += `/${segments[i]}`;
    breadcrumbItems.push(
      <span key={i}>
        {i === 0 ? (
          props.app.split("/")[0]
        ) : (
          <Link to={accumulatedPath}>{generateDisplayName(segments[i])}</Link>
        )}
        {i < segments.length - 1 && " / "}
      </span>
    );
  }

  return (
    <AppBar className={classes.appBar + appBarClasses}>
      <Toolbar
        className={classes.container}
        style={{ display: "flex", justifyContent: "space-between" }}
      >
        <div>
          {/* Render back button or drawer toggle button based on conditions */}
          {includeHistory || windowWidth ? (
            <Button
              onClick={() => handleClose()}
              color="white"
              aria-label="edit"
              justIcon
              round
            >
              {props.openDrawer ? (
                <MoreVertIcon style={{ color: "#000" }} fontSize="large" />
              ) : (
                <ListIcon style={{ color: "#000" }} fontSize="large" />
              )}
            </Button>
          ) : includeHistory ? (
            <Button
              onClick={() => handleClose()}
              color="white"
              aria-label="edit"
              justIcon
              round
            >
              {props.openDrawer ? (
                <MoreVertIcon style={{ color: "#000" }} fontSize="large" />
              ) : (
                <ListIcon style={{ color: "#000" }} fontSize="large" />
              )}
            </Button>
          ) : (
            <Button
              onClick={() => handleBackMobile()}
              color="white"
              aria-label="edit"
              justIcon
              round
            >
              <ArrowBackIcon style={{ color: "#000" }} fontSize="large" />
            </Button>
          )}
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
          {/* Render breadcrumb items */}
          {breadcrumbItems}
        </div>
        <div className={classes.hideHeader}>
          {/* Render admin navbar links */}
          <AdminNavbarLinks {...props} />
        </div>
      </Toolbar>
    </AppBar>
  );
}

// Define prop types for the Header component
Header.propTypes = {
  color: PropTypes.oneOf(["primary", "info", "success", "warning", "danger"]),
  rtlActive: PropTypes.bool,
  handleDrawerToggle: PropTypes.func,
  setopen: PropTypes.func,
  openDrawer: PropTypes.any,
  setState: PropTypes.func,
  menuData: PropTypes.object,
  logout: PropTypes.func,
  displayName: PropTypes.string,
  app: PropTypes.string,
  setBackToSearchFromDetails: PropTypes.func,
  setCallRolePermission: PropTypes.func,
  setOpenDrawerHover: PropTypes.func,
};

// Map Redux state to component props
const mapStateToProps = ({ userRole, app }) => ({
  menuData: userRole,
  app: app.appBarName,
});

// Connect the component to Redux and export it
export default connect(mapStateToProps, { logout, setBackToSearchFromDetails })(
  Header
);
